import { useState } from "react";
import { useTranslation } from "react-i18next";
import { Icon } from "semantic-ui-react";

import { CUSTOM_CONFIRM_TYPES, CustomConfirm } from "components";
import { useAppointmentPinMutation } from "components/AppointmentPin/hooks";

import type { Appointment, DealerLocation } from "models";

type AppointmentPinProps = {
  appointment: Appointment;
  location?: DealerLocation;
  readOnly?: boolean;
};

export const AppointmentPin = ({ appointment: { id, is_pinned, is_pinned_manually }, location, readOnly = false }: AppointmentPinProps) => {
  const { t } = useTranslation();

  const [showConfirmModal, setShowConfirmModal] = useState(false);
  const { mutate: handleChangeAppointmentPin, isPending } = useAppointmentPinMutation(location);

  const handleUnpinAppointment = (e: React.MouseEvent<HTMLButtonElement, MouseEvent>) => {
    e.preventDefault();
    handleChangeAppointmentPin({ appointment_id: id, is_pinned: false, is_pinned_manually: false });
    setShowConfirmModal(false);
  };

  const handlePinAppointment = (e: React.MouseEvent<HTMLElement, MouseEvent>) => {
    e.preventDefault();
    is_pinned ? setShowConfirmModal(true) : handleChangeAppointmentPin({ appointment_id: id, is_pinned: true, is_pinned_manually: true });
  };

  const isAppointmentPinDisabled =
    is_pinned && !is_pinned_manually ? location?.is_automatically_pinned_appointments_readonly : !location?.is_manually_pinning_appointments_enabled;

  if (readOnly)
    return (
      <span>
        <Icon color={is_pinned_manually ? "red" : undefined} className={`${!is_pinned ? "circle regular" : "pin"} appointment-pin appointment-pin-disabled`} />
      </span>
    );

  return (
    <span onClick={e => e.stopPropagation()}>
      <Icon
        color={is_pinned_manually ? "red" : undefined}
        className={`${!is_pinned ? "circle regular" : "pin"} appointment-pin ${isAppointmentPinDisabled ? "appointment-pin-disabled" : ""}`}
        onClick={handlePinAppointment}
      />
      <CustomConfirm
        isLoading={isPending}
        isOpen={showConfirmModal}
        type={CUSTOM_CONFIRM_TYPES.Warning}
        handleConfirm={handleUnpinAppointment}
        handleCancel={() => setShowConfirmModal(false)}
        confirmMsg={t("v8_unpin_appointment_confirm_message", "Do you want to unpin the selected appointment? This action will affect other users as well.")}
      />
    </span>
  );
};
